import http from '@services/HTTP'
import type { AuthResponse } from '@services/client/auth/useSignUp'
import { isValidJWT } from '@services/client/patient'
import { queryClient } from '@services/query'
import { logLogout, setUserId } from '@utils/analytics'
import { isReturningFromApple } from '@utils/ssoPopup'
import { safeStorage } from '@utils/storage'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { clearPatientAnswers } from './answers'
import { useCheckoutStore } from './checkout'
import { usePatientStore } from './patient'

type AuthCallback = {
  onAuth?: () => void
  onClose?: () => void
  login_place?: string
  showOrderCopy?: boolean
  // Where onAuth would have taken them. Apple's redirect reloads the page and takes
  // onAuth with it, so callers that navigate name the destination here as well.
  returnTo?: string
}
interface AuthState {
  isLogged: boolean
  token: string
  renew: string
  userHash: string
  isHydrated: boolean
  setHydrated: () => void
  updateToken: (auth: AuthResponse) => void
  logOut: () => void
  showAuth: boolean
  type: 'login' | 'signup'
  showResetPassword: boolean
  showOTP: boolean
  authCallback?: AuthCallback
  loginMethod: 'notSso' | 'sso' | null
  setLoginMethod: (method: 'notSso' | 'sso') => void
  requestAuth: (params?: AuthCallback & { type?: 'login' | 'signup'; login_place?: string }) => void
  onAuthClose: () => void
  onSuccessAuth: () => void
  requestResetPassword: () => void
  closeResetPassword: () => void
  requestOTP: () => void
  closeOTP: () => void
}

export const useAuthState = create<AuthState>()(
  persist(
    (set, get) =>
      ({
        isLogged: false,
        token: null,
        renew: null,
        userHash: null,
        isHydrated: false,
        showAuth: typeof window === 'undefined' ? false : new URLSearchParams(window.location.search).get('dialog') === 'auth',
        type: 'login',
        showResetPassword: typeof window === 'undefined' ? false : new URLSearchParams(window.location.search).get('dialog') === 'reset-password',
        showOTP: false,
        loginMethod: null,
        setLoginMethod: method => {
          set({ loginMethod: method })
        },
        updateToken: (auth: AuthResponse) => {
          const { cartOwnerHash } = useCheckoutStore.getState()
          if (cartOwnerHash && cartOwnerHash !== auth.user) {
            useCheckoutStore.getState().reset()
          }
          // A different account signing in on this device leaves the previous customer's patient
          // and answers in the store, which the next add to cart would attach to their item. A
          // renewed token carries the same user, and a guest signing in has none stored yet, so
          // neither clears the consultation they are in the middle of.
          const previousUserHash = get().userHash
          if (previousUserHash && previousUserHash !== auth.user) {
            usePatientStore.getState().reset()
            clearPatientAnswers()
          }
          set({
            isLogged: true,
            token: auth.token,
            renew: auth.renew,
            userHash: auth.user
          })
          // Revalidate coupon after login
          useCheckoutStore.getState().revalidateCoupon()
        },
        logOut: () => {
          set({
            isLogged: false,
            token: null,
            renew: null,
            userHash: null,
            loginMethod: null
          })
          usePatientStore.getState().reset()
          clearPatientAnswers()
          useCheckoutStore.getState().reset()

          safeStorage.removeItem('user')
          queryClient.clear()
          logLogout()
          setUserId(null)
        },
        requestAuth: params => {
          set({
            showAuth: true,
            type: params?.type || 'login',
            authCallback: {
              ...params
            }
          })
        },
        onSuccessAuth: () => {
          get().authCallback?.onAuth?.()
          useSSOState.getState().setSSOError(null)
          set({
            showAuth: false,
            showOTP: false,
            authCallback: null
          })
        },
        onAuthClose: () => {
          get().authCallback?.onClose?.()
          // The SSO error lives outside the modal now, so closing has to clear it or
          // the next open shows the last attempt's banner. Cleared on close rather
          // than on open, so an error raised while the modal was shut still surfaces.
          useSSOState.getState().setSSOError(null)
          set({
            showAuth: false,
            type: 'login',
            authCallback: null
          })
        },
        requestResetPassword: () => {
          set({
            showResetPassword: true
          })
        },
        requestOTP: () => {
          set({
            showOTP: true
          })
        },
        closeResetPassword: () => {
          set({
            showResetPassword: false
          })
        },
        closeOTP: () => {
          set({
            showOTP: false
          })
        },
        setHydrated: () => {
          set({ isHydrated: true })
        }
      }) as AuthState,
    {
      name: 'auth_state',
      storage: createJSONStorage(() => safeStorage),
      version: 1,
      skipHydration: true,
      partialize: state => ({
        isLogged: state.isLogged,
        token: state.token,
        renew: state.renew,
        userHash: state.userHash,
        loginMethod: state.loginMethod
      })
    }
  )
)

useAuthState.persist.onFinishHydration(() => {
  useAuthState.getState().setHydrated()
})

interface SSOState {
  ssoPending: boolean
  ssoError: Error | null
  // A redirect sign-in is finishing on this page load, so the login modal stays shut
  ssoCompleting: boolean
  setSSOPending: (ssoPending: boolean) => void
  setSSOError: (ssoError: Error | null) => void
  setSSOCompleting: (ssoCompleting: boolean) => void
}

// Shared SSO progress, kept in its own unpersisted store. The Apple result handler
// runs at layout level so a reload can't unmount it, while the spinner and error
// banner render inside the login modal. Persisting it would be wrong twice over: a
// stale pending flag leaves the button spinning on every later visit, and an Error
// serialises to an empty object, which renders as a blank error banner.
export const useSSOState = create<SSOState>()(set => ({
  ssoPending: false,
  ssoError: null,
  ssoCompleting: isReturningFromApple(),
  setSSOPending: (ssoPending: boolean) => set({ ssoPending }),
  setSSOError: (ssoError: Error | null) => set({ ssoError }),
  setSSOCompleting: (ssoCompleting: boolean) => set({ ssoCompleting })
}))

// How many AuthGuard page spinners are mounted, so the auth modal can skip its own. Unpersisted.
export const useGuardSpinners = create<{ count: number }>()(() => ({ count: 0 }))

let promise: Promise<string | void> | null = null

export const getToken = async (): Promise<string | void> => {
  if (promise) return promise
  const { token, renew, userHash } = useAuthState.getState()
  if (!token || !renew) return null

  if (!isValidJWT(token)) {
    promise = http
      .post<{ jwt_renew: string; user_hash: string }, AuthResponse>(
        '/authenticate/token/renew',
        {
          jwt_renew: renew,
          user_hash: userHash
        },
        {
          headers: {
            Authorization: null
          }
        }
      )
      .then(res => {
        useAuthState.getState().updateToken(res)
        promise = null
        return res.token
      })
      .catch((e): void => {
        console.warn('Error renewing token, logging out', e)
        useAuthState.getState().logOut()
        queryClient.removeQueries({ queryKey: ['user'], type: 'all' })
        promise = null
        return null
      })

    return promise
  }
  return token
}
